import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Shared profile modal for About Us Company / Team pages.
 * Preserves existing open/close transition behavior.
 */
export function useAboutProfileModal() {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!selectedProfile) return undefined;

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setIsProfileModalOpen(true));
    });

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeProfileModal();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open only when profile changes
  }, [selectedProfile]);

  const openProfileModal = (profile) => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsProfileModalOpen(false);
    setSelectedProfile(profile);
  };

  const clearProfileModal = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setSelectedProfile(null);
    setIsProfileModalOpen(false);
  };

  const closeProfileModal = () => {
    setIsProfileModalOpen(false);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(clearProfileModal, 400);
  };

  const handleProfileModalTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== "opacity") return;
    if (isProfileModalOpen) return;
    clearProfileModal();
  };

  const profileModal =
    selectedProfile &&
    createPortal(
      <div
        className={`agc_profile_modal_overlay${isProfileModalOpen ? " is-open" : ""}`}
        onClick={closeProfileModal}
        onTransitionEnd={handleProfileModalTransitionEnd}
        role="dialog"
        aria-modal="true"
        aria-label={`${selectedProfile.name} profile`}
      >
        <div className="agc_profile_modal_content" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="agc_profile_modal_close"
            onClick={closeProfileModal}
            aria-label="Close profile"
          >
            &times;
          </button>
          <div className="agc_profile_modal_body">
            {selectedProfile.image ? (
              <div
                className={`agc_profile_modal_img_wrapper${
                  selectedProfile.isLeadership ? " agc_profile_modal_img_wrapper_small" : ""
                }`}
              >
                <img
                  src={selectedProfile.image}
                  alt={selectedProfile.name}
                  className="agc_profile_modal_img"
                />
              </div>
            ) : null}
            <div className="agc_profile_modal_info">
              <h3>{selectedProfile.name}</h3>
              <span className="agc_role">{selectedProfile.role}</span>
              <p>{selectedProfile.description}</p>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );

  return { openProfileModal, profileModal };
}
